import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { Badge } from '@/components/shadcn/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { RestSchemas } from '@/contexts/QueryProvider'
import { getInitials } from '@/lib/utils'
import { ColumnDef } from '@tanstack/react-table'
import { format as formatDate } from 'date-fns'

type MessagesSupportAuditLogEntry = RestSchemas['Messages.Support.AuditLogEntry']
type MessagesPlatformPlatformUserWithAudit = RestSchemas['Messages.Platform.PlatformUserWithAudit']
type AuditLogTableRow = MessagesSupportAuditLogEntry & { user: MessagesPlatformPlatformUserWithAudit | undefined }

function JsonAuditCell({ value }: { value?: string | null }) {
  if (!value) {
    return <span className="text-base text-muted-foreground">-</span>
  }

  const prettyValue = (() => {
    try {
      return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
      return value
    }
  })()

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="block w-[200px] overflow-hidden text-ellipsis whitespace-nowrap rounded-l-full rounded-r-full border border-border px-2 py-1 text-left font-mono text-xs text-muted-foreground hover:text-foreground"
          title="Click to view JSON"
        >
          {value}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" className="w-[560px] p-3" portal>
        <pre className="max-h-[360px] overflow-auto whitespace-pre font-mono text-xs leading-5">{prettyValue}</pre>
      </PopoverContent>
    </Popover>
  )
}

export const columns: ColumnDef<AuditLogTableRow>[] = [
  {
    id: 'user',
    accessorKey: 'user',
    header: ({ column }) => <SortingHeaderButton column={column}>Made by</SortingHeaderButton>,
    cell: ({ row }) => {
      const givenName = row.original.user?.givenName ?? ''
      const familyName = row.original.user?.familyName ?? ''
      const email = row.original.user?.email ?? ''
      const displayName = `${givenName} ${familyName}`.trim() || 'Unknown user'

      return (
        <div className="group flex items-start gap-x-1">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full">
            {/* <IconPersonOutline className="size-5 text-muted-foreground" /> */}
            <Avatar className="size-8 overflow-visible">
              <AvatarFallback className="bg-accent text-accent-foreground">{getInitials(displayName)}</AvatarFallback>
            </Avatar>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-x-1">
              <span className="text-base">{displayName} </span>
              <Badge variant="secondary" className="h-4 font-normal text-foreground">
                {row.original.user?.id}
              </Badge>
            </div>
            <span className="text-xs font-light leading-[1.1] text-muted-foreground">{email}</span>
          </div>
        </div>
      )
    },
    sortingFn: (rowA, rowB) => {
      const nameA = `${rowA.original.user?.givenName ?? ''} ${rowA.original.user?.familyName ?? ''}`.trim()
      const nameB = `${rowB.original.user?.givenName ?? ''} ${rowB.original.user?.familyName ?? ''}`.trim()
      return nameA.localeCompare(nameB)
    },
    size: 250,
  },
  {
    id: 'date',
    accessorKey: 'date',
    header: ({ column }) => <SortingHeaderButton column={column}>Date & Time</SortingHeaderButton>,
    cell: ({ row }) => (
      <div className="flex justify-start">
        <div className="flex flex-col items-end">
          <div>
            <Badge variant="secondary" className="h-4 font-normal text-foreground">
              {row.original.date ? formatDate(row.original.date, 'PP') : ''}
            </Badge>
          </div>
          <div>
            <Badge variant="outline" className="h-4 font-normal">
              {row.original.date ? formatDate(row.original.date, 'p') : ''}
            </Badge>
          </div>
        </div>
      </div>
    ),
    sortingFn: (rowA, rowB) => (rowA.original.date ? rowA.original.date.localeCompare(rowB.original.date ?? '') : 0),
  },
  {
    id: 'type',
    header: ({ column }) => <SortingHeaderButton column={column}>Type</SortingHeaderButton>,
    accessorKey: 'type',
    cell: ({ row }) => (
      <Badge variant="secondary" className="font-normal text-foreground">
        {row.original.type ?? ''}
      </Badge>
    ),
    sortingFn: (rowA, rowB) => (rowA.original.type ?? '').localeCompare(rowB.original.type ?? ''),
  },
  {
    id: 'before',
    accessorKey: 'before',
    header: 'Before',
    cell: ({ row }) => <JsonAuditCell value={row.original.before} />,
    enableSorting: false,
    size: 220,
    minSize: 180,
  },
  {
    id: 'after',
    accessorKey: 'after',
    header: 'After',
    cell: ({ row }) => <JsonAuditCell value={row.original.after} />,
    enableSorting: false,
    size: 220,
    minSize: 180,
  },
  {
    id: 'affectedTable',
    accessorKey: 'affectedTable',
    header: ({ column }) => <SortingHeaderButton column={column}>DB Table</SortingHeaderButton>,
    cell: ({ row }) => (
      <Badge variant="outline" className="font-normal">
        {row.original.affectedTable ?? ''}
      </Badge>
    ),
    sortingFn: (rowA, rowB) => (rowA.original.affectedTable ?? '').localeCompare(rowB.original.affectedTable ?? ''),
  },
  {
    id: 'affectedRecordId',
    accessorKey: 'affectedRecordId',
    header: ({ column }) => <SortingHeaderButton column={column}>Record ID</SortingHeaderButton>,
    cell: ({ row }) => (
      <Badge variant="secondary" className="font-normal text-foreground">
        {row.original.affectedRecordId ?? ''}
      </Badge>
    ),
    sortingFn: (rowA, rowB) => (rowA.original.affectedRecordId ?? 0) - (rowB.original.affectedRecordId ?? 0),
  },
]
